import { CompetencyAppliesTo } from "types/graphql-schema";

import { UseLabelType } from "@apps/use-label/use-label";

import { formatNameList } from "./competency-sharing";
import { describeRoleHolders } from "./helpers";

type DepartmentCompetency = {
  id: number;
  title: string;
  appliesTo: CompetencyAppliesTo;
  appliesToIcRoles?: boolean | null;
  appliesToManagementRoles?: boolean | null;
  teamIds: ReadonlyArray<number>;
  careerTrackIds: ReadonlyArray<number>;
};

// What deleting a department takes with it. On the backend a department
// deletes its career tracks (and so their roles, criteria, responsibilities,
// role assignments and review questions), and drops itself and those tracks
// from every competency's scope.
export const getDepartmentDeletionSummary = ({
  departmentId,
  departmentTitle,
  memberCount,
  tracks,
  competencies,
  label,
}: {
  departmentId: number;
  departmentTitle: string;
  memberCount: number;
  tracks: ReadonlyArray<{
    id: number;
    title: string;
    roles: ReadonlyArray<{
      assignedUsers: ReadonlyArray<{ id: number; name: string }>;
    }>;
  }>;
  competencies: ReadonlyArray<DepartmentCompetency>;
  label: UseLabelType;
}) => {
  const roles = tracks.flatMap((track) => track.roles);
  // "1 career track: Backend, with its 2 roles. Ada loses it."
  const trackLine = [
    `${tracks.length} career ${tracks.length === 1 ? "track" : "tracks"}: ${formatNameList(
      tracks.map((track) => track.title),
    )}`,
    roles.length > 0
      ? `, with ${tracks.length === 1 ? "its" : "their"} ${roles.length} ${
          roles.length === 1 ? "role" : "roles"
        }. ${describeRoleHolders(
          roles.flatMap((role) => role.assignedUsers),
          roles.length,
        )}`
      : ".",
  ].join("");
  const deleted = [
    tracks.length > 0 ? trackLine : null,
    roles.length > 0
      ? `${label("review", {
          capitalize: true,
        })} questions and answers about those roles.`
      : null,
  ].filter((line): line is string => line !== null);

  // A competency scoped to some roles, left with no department or track to
  // name, falls back on its role type alone (get_role_filter on the backend):
  // with one it reaches that type across the organization, without one it
  // reaches nobody. Either is a surprise, so each is said.
  const deletedTrackIds = new Set(tracks.map((track) => track.id));
  const changed = competencies.flatMap((competency) => {
    if (competency.appliesTo !== CompetencyAppliesTo.SomeRoles) {
      return [];
    }
    const touched =
      competency.teamIds.includes(departmentId) ||
      competency.careerTrackIds.some((id) => deletedTrackIds.has(id));
    const leftTeams = competency.teamIds.filter((id) => id !== departmentId);
    const leftTracks = competency.careerTrackIds.filter(
      (id) => !deletedTrackIds.has(id),
    );
    if (!touched || leftTeams.length > 0 || leftTracks.length > 0) {
      return [];
    }
    const { appliesToIcRoles: ic, appliesToManagementRoles: managers } =
      competency;
    if (!ic && !managers) {
      return [
        `${competency.title} stops applying to any role: it was written only for ${departmentTitle}.`,
      ];
    }
    const who =
      ic && managers ? "role" : managers ? "manager" : "individual contributor";
    return [
      `${competency.title} applies to every ${who} in your ${label(
        "organization",
      )} instead.`,
    ];
  });

  const kept =
    memberCount > 0
      ? `Its ${memberCount} ${memberCount === 1 ? "member stays" : "members stay"} in your ${label(
          "organization",
        )}.`
      : null;

  return { title: `Delete ${departmentTitle}?`, deleted, changed, kept };
};
